import { Box } from '@mui/system';
import { Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    CountryBar,
    CountryItem,
} from 'src/components/country-bar';

import { useCountryData } from 'src/components/visibility';

import { HEADER } from 'src/layouts/config-layout';

import {
    useCurrentWorkspace,
} from 'src/context/reducers/app-settings';

import {
    setFilterCountry,
} from 'src/context/reducers/filter-settings/filterSlice';

import {
    useGetConversionsKpisQuery,
} from 'src/context/api/conversions/api';

import { CountryTabLabel } from '../explore/CountryTabLabel';
import { usePersistedCountries } from '../explore/usePersistedCountries';

import { PAGE_PADDING_X } from './constants';

import FunnelKpiCard from './FunnelKpiCard';
import ConversionsDistributionChart from './ConversionsDistributionChart';

import ConversionsGrid from './ConversionsGrid';

export default function Conversions() {
    const dispatch = useDispatch();

    const filterStore = useSelector(
        (state: any) => state.filters
    );

    const workspace: any = useCurrentWorkspace();

    const {
        countriesData: countries,
        isLoading: isLoadingCountries,
    } = useCountryData();

    const { t } = useTranslation();

    const [
        countryList,
        setCountryList,
    ] = useState<CountryItem[]>([]);


    // -------------------------------------------------------
    // Countries
    // -------------------------------------------------------

    useEffect(() => {
        if (
            countryList.length === 0 &&
            countries.length > 0
        ) {
            setCountryList(countries);
        }
    }, [
        countries,
        countryList.length,
    ]);


    const handleRestore = useCallback(
        (names: string[]) => {
            dispatch(
                setFilterCountry(names)
            );
        },
        [dispatch]
    );


    const { persist } =
        usePersistedCountries(
            countryList,
            handleRestore
        );


    const handleCountriesChange = (
        names: string[]
    ) => {
        dispatch(
            setFilterCountry(names)
        );

        persist(names);
    };


    // -------------------------------------------------------
    // Conversions API
    // -------------------------------------------------------
    //
    // IMPORTANT :
    // dimension = date
    //
    // Le timestep continue de gérer :
    // - date
    // - year_week
    // - year_month
    //
    // mais on reste dans une dimension temporelle.
    // -------------------------------------------------------

    const {
        data: conversionsData,
        error: conversionsError,
        isFetching: isFetchingConversions,
    } = useGetConversionsKpisQuery(
        {
            workspaceId:
                workspace?.id,

            startDate:
                filterStore?.start?.split('T')[0],

            endDate:
                filterStore?.end?.split('T')[0],

            compareStartDate:
                filterStore?.compareStart?.split('T')[0],

            compareEndDate:
                filterStore?.compareEnd?.split('T')[0],

            countries:
                filterStore?.countries ?? [],

            devices:
                filterStore?.devices ?? [],

            platforms:
                filterStore?.platforms ?? [],

            dimension: 'date',

            timestep:
                filterStore?.timestep ??
                'year_week',
        },
        {
            skip:
                !workspace?.id ||
                !filterStore?.start ||
                !filterStore?.end,
        }
    );


    // -------------------------------------------------------
    // Workspace model
    // -------------------------------------------------------

    const isLead =
        (
            workspace?.template ?? ''
        )
            .toLowerCase()
            .includes('lead');


    const isCustom =
        Boolean(
            workspace?.custom
        );


    const organicMode =
        workspace
            ?.organic_conversion_mode ===
            'modelized'
            ? 'modelized'
            : 'total';


            console.log( workspace
             , "fzeijfezifji")
    // -------------------------------------------------------
    // KPI cards
    // -------------------------------------------------------

    const filteredStats = [
        {
            title:
                isLead
                    ? 'Blended Conversions'
                    : 'Blended Revenue',

            value:
                conversionsData
                    ?.summary
                    ?.blended ?? 0,

            compare:
                filterStore?.compare
                    ? conversionsData
                        ?.summary_compare
                        ?.blended
                    : undefined,

            definition:
                isLead
                    ? t(
                        'dashboard.global.conversions.blendedConversions'
                    )
                    : t(
                        'dashboard.global.conversions.blendedRevenue'
                    ),

            isPercent: false,

            isCurrency:
                !isLead,
        },

        {
            title:
                isLead
                    ? 'Paid Conversions'
                    : 'Paid Revenue',

            value:
                conversionsData
                    ?.summary
                    ?.paid ?? 0,

            compare:
                filterStore?.compare
                    ? conversionsData
                        ?.summary_compare
                        ?.paid
                    : undefined,

            definition:
                isLead
                    ? isCustom
                        ? t(
                            'dashboard.global.conversions.paidConversionsCustom'
                        )
                        : t(
                            'dashboard.global.conversions.paidConversions'
                        )
                    : isCustom
                        ? t(
                            'dashboard.global.conversions.paidRevenueCustom'
                        )
                        : t(
                            'dashboard.global.conversions.paidRevenue'
                        ),

            isPercent: false,

            isCurrency:
                !isLead,
        },

        {
            title:
                isLead
                    ? 'Organic Conversions'
                    : 'Organic Revenue',

            value:
                conversionsData
                    ?.summary
                    ?.organic ?? 0,

            compare:
                filterStore?.compare
                    ? conversionsData
                        ?.summary_compare
                        ?.organic
                    : undefined,

            definition:
                isLead
                    ? isCustom
                        ? t(
                            'dashboard.global.conversions.organicConversionsCustom'
                        )
                        : organicMode ===
                            'modelized'
                            ? t(
                                'dashboard.global.conversions.organicConversionsModelized'
                            )
                            : t(
                                'dashboard.global.conversions.organicConversionsTotal'
                            )
                    : isCustom
                        ? t(
                            'dashboard.global.conversions.organicRevenueCustom'
                        )
                        : organicMode ===
                            'modelized'
                            ? t(
                                'dashboard.global.conversions.organicRevenueModelized'
                            )
                            : t(
                                'dashboard.global.conversions.organicRevenueTotal'
                            ),

            isPercent: false,

            isCurrency:
                !isLead,
        },

        {
            title:
                'Blended CVR',

            value:
                conversionsData
                    ?.summary
                    ?.blended_cvr ?? 0,

            compare:
                filterStore?.compare
                    ? conversionsData
                        ?.summary_compare
                        ?.blended_cvr
                    : undefined,

            definition:
                t(
                    'dashboard.global.conversions.blendedCvr'
                ),

            isPercent: true,

            isCurrency: false,
        },
        {
            title: 'Blended Cost per Conversion',

            value:
                conversionsData
                    ?.summary
                    ?.cost_per_blended_conv ?? 0,

            compare:
                filterStore?.compare
                    ? conversionsData
                        ?.summary_compare
                        ?.cost_per_blended_conv
                    : undefined,

            definition:
                t(
                    'dashboard.global.conversions.blendedCostPerConv'
                ),

            isPercent: false,

            isCurrency: true,

            invertedColor: true,

            hideInfinity: true,
        },
    ];


    // -------------------------------------------------------
    // Render
    // -------------------------------------------------------

    return (
        <Box px="24px">
            <CountryBar
                countries={countryList}
                multiple
                selectedCountries={
                    filterStore?.countries ??
                    []
                }
                onCountriesChange={
                    handleCountriesChange
                }
                loading={
                    isLoadingCountries ||
                    isFetchingConversions
                }
                stickyTop={
                    HEADER.H_DESKTOP * 2
                }
                backgroundColor="background.paper"
                topGap={60}
                reserveGapWhenHidden
                contentPaddingX={
                    PAGE_PADDING_X
                }
                tabSpacing={12}
                renderCountryLabel={(
                    country,
                    isSelected
                ) => (
                    <CountryTabLabel
                        country={country}
                        isSelected={
                            isSelected
                        }
                    />
                )}
            />

            {/* KPI CARDS */}

            <Grid
                container
                spacing={1.5}
            >
                {(
                    isFetchingConversions ||
                        (
                            !conversionsData &&
                            !conversionsError
                        )
                        ? [1, 2, 3, 4, 5]
                        : filteredStats
                ).map(
                    (
                        stat: any,
                        index: number
                    ) => (
                        <Grid
                            item
                            key={index}
                            xs={6}
                            md={4}
                            sx={{
                                '@media (min-width:1200px)': {
                                    flexBasis: '20%',
                                    maxWidth: '20%',
                                },
                            }}
                        >
                            <FunnelKpiCard
                                isFetching={
                                    isFetchingConversions ||
                                    (
                                        !conversionsData &&
                                        !conversionsError
                                    )
                                }
                                title={
                                    stat?.title
                                }
                                compare={
                                    stat?.compare
                                }
                                definition={
                                    stat?.definition
                                }
                                invertedColor={
                                    stat?.invertedColor
                                }
                                isPercent={
                                    stat?.isPercent
                                }
                                isCurrency={
                                    stat?.isCurrency
                                }
                                total={
                                    stat?.value
                                }
                                hideInfinity={
                                    stat?.hideInfinity
                                }
                            />
                        </Grid>
                    )
                )}
            </Grid>

            {/* CONVERSIONS GRAPH */}

            <ConversionsDistributionChart
                data={
                    conversionsData
                }
                error={
                    conversionsError
                }
                isFetching={
                    isFetchingConversions
                }
            />

            {/* CONVERSIONS GRID */}

            <ConversionsGrid />
        </Box>
    );
}